import * as THREE from 'three';
import { hexDist, hexRing, hexToWorld, type Hex, type MapDef } from '@somnhold/sim';
import { GlowLayer } from '../engine/glow.ts';
import { Kit, place } from '../seasons/kit.ts';
import { looksRng, TAU, type OmenCtx, type OmenScene } from './common.ts';

/**
 * The sunken camp (La Quemadura 2, docs/QUEMADURA.md): on the Burn's black glass, a camp centuries old half swallowed
 * by it. Tents of hard leather gone black, leaning where the glass took them; spears and blades that melted where
 * they stood, still upright; two or three ballistas of a build nobody in Somnhold uses (a double recurved bow on a
 * long rail, a ram's head at its end), sunk at an angle; the ring of a cold fire pit; a pole with a rag of the old
 * red still on it. Off the roads and the chests, mostly out past the clearing so the fight reads clear.
 *
 * Cheap like the graveyard: every static piece is one merged mesh (vertex colours); the fire pit's last embers and a
 * dull glow at each tent's foot one glow layer. No lights.
 */

const HIDE = ['#2c2320', '#352a24', '#261e1b', '#3b2e27'];
const POLE = ['#3a2e26', '#312720', '#40332a'];
const POOL = ['#0e0b0d', '#141012', '#0b090b'];
const IRON = ['#4a4644', '#55504c', '#403c3a'];
const BRONZE = ['#4e4038', '#5a4a3e', '#463830'];
const SLAG = ['#2a2224', '#33282a'];
const STONE = ['#4a4446', '#55504f', '#3f3a3b'];
const RAG = ['#6b2a24', '#5e2420', '#74302a'];
const EMBER = new THREE.Color('#ff6a3a');

const P = (() => {
  const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 6);
  const box = new THREE.BoxGeometry(1, 1, 1);
  const rock = new THREE.DodecahedronGeometry(0.5, 0);
  const disc = new THREE.CylinderGeometry(0.5, 0.5, 0.02, 14);
  const blob = new THREE.IcosahedronGeometry(0.5, 0);
  const cone = new THREE.ConeGeometry(0.5, 1, 5);
  // A bow limb: a curved quarter ring lying flat (the ballista's arms).
  const limb = new THREE.TorusGeometry(0.5, 0.06, 4, 8, Math.PI * 0.5).rotateX(Math.PI / 2);
  return { cyl, box, rock, disc, blob, cone, limb };
})();

const pick = <T>(list: T[], r: number): T => list[Math.floor(r * list.length) % list.length] as T;

/**
 * `sparse` (La Quemadura 3, by the old trap): what is left of the camp further in, a few pieces only and nothing inside
 * the clearing; `avoid` keeps them off a stretch of ground (the harpoon's line).
 */
export const buildCamp = (ctx: OmenCtx, map: MapDef, chests: { x: number; y: number }[], o: { sparse?: boolean; avoid?: (p: THREE.Vector3) => boolean } = {}): OmenScene => {
  const group = new THREE.Group();
  group.name = 'camp';
  const rnd = looksRng(0xca4b + map.radius * 17);
  const body = new Kit();
  const road: Hex[] = map.sectors.flatMap((s) => s.path);
  const nearRoad = (h: Hex, d = 0): boolean => road.some((r) => hexDist(r, h) <= d);
  const ground = (x: number, z: number): THREE.Vector3 => new THREE.Vector3(x, ctx.ground(x, z), z);
  const chestAt = chests.map((c) => ctx.at(c.x, c.y, 0));
  const clear = (p: THREE.Vector3, r: number): boolean => chestAt.every((c) => (c.x - p.x) ** 2 + (c.z - p.z) ** 2 > r * r);
  const glow = new GlowLayer(48);
  const embers: THREE.Vector3[] = [];
  /** Glows under the sunk pieces (still) and the fire's embers (flickering). */
  const dull: { p: THREE.Vector3; s: number; o: number }[] = [];

  const free: Hex[] = [];
  const outer = Math.min(map.bastion + 4, map.radius - 2);
  for (let r = map.bastion + 1; r <= outer; r++) for (const h of hexRing(r)) if (!nearRoad(h, 1)) free.push(h);
  const inner: Hex[] = [];
  for (let r = 2; r <= map.bastion; r++) for (const h of hexRing(r)) if (!nearRoad(h, 0)) inner.push(h);
  const take = (pool: Hex[]): THREE.Vector3 | null => {
    for (let k = 0; k < 12 && pool.length; k++) {
      const i = Math.floor(rnd() * pool.length);
      const h = pool[i] as Hex;
      pool.splice(i, 1);
      const w = hexToWorld(h.q, h.r);
      const p = ctx.at(w.x + (rnd() - 0.5) * 400, w.y + (rnd() - 0.5) * 400, 0);
      if (!clear(p, 0.9) || o.avoid?.(p)) continue;
      // Nothing big right next to another big piece.
      for (const o of [...pool]) if (hexDist(o, h) <= 1 && pool === free) pool.splice(pool.indexOf(o), 1);
      return ground(p.x, p.z);
    }
    return null;
  };

  /** Where the glass swallowed something: a dark glossy pool, a little wider than it. */
  const pool = (p: THREE.Vector3, r: number): void => {
    // Sunk a little: the glass round it is uneven, and a disc laid on top of it reads as a plate standing up.
    body.add(P.disc, pick(POOL, rnd()), place(p.x, ctx.ground(p.x, p.z) - 0.03, p.z, rnd() * TAU, new THREE.Vector3(r * 1.6, 3, r * 1.3)));
  };

  /** A tent of hard leather gone black: an A-frame leaning where the glass took it, its foot sunk. */
  const tent = (p: THREE.Vector3): void => {
    const yaw = rnd() * TAU;
    const m = place(p.x, p.y - 0.12, p.z, yaw, 1, (rnd() - 0.5) * 0.5, (rnd() - 0.5) * 0.35);
    const len = 1.3 + rnd() * 0.5;
    const tall = 0.85 + rnd() * 0.25;
    for (const s of [-1, 1]) body.add(P.box, pick(HIDE, rnd()), m, place(s * 0.34, tall / 2, 0, 0, new THREE.Vector3(0.05, tall * 1.12, len), 0, s * -0.62));
    // The ridge pole sticking out at both ends, and the front poles.
    body.add(P.cyl, pick(POLE, rnd()), m, place(0, tall + 0.02, 0, 0, new THREE.Vector3(0.06, len + 0.4, 0.06), Math.PI / 2));
    for (const z of [-len / 2, len / 2]) for (const s of [-1, 1]) body.add(P.cyl, pick(POLE, rnd()), m, place(s * 0.3, tall / 2, z, 0, new THREE.Vector3(0.05, tall * 1.15, 0.05), 0, s * -0.6));
    // A torn flap hanging off the back.
    body.add(P.box, pick(HIDE, rnd()), m, place(0.2, tall * 0.35, len / 2 + 0.04, 0.3, new THREE.Vector3(0.35, tall * 0.6, 0.03), 0.2));
    pool(p, 0.95);
    dull.push({ p: p.clone().setY(p.y + 0.05), s: 1.3, o: 0.05 });
  };

  /** Spears and blades that melted where they stood: a few upright at angles, drooping at the top, a drip at the foot. */
  const weapons = (p: THREE.Vector3): void => {
    const n = 2 + Math.floor(rnd() * 3);
    for (let i = 0; i < n; i++) {
      const x = p.x + (rnd() - 0.5) * 0.7;
      const z = p.z + (rnd() - 0.5) * 0.7;
      const y = ctx.ground(x, z);
      const tall = 0.7 + rnd() * 0.6;
      const m = place(x, y - 0.05, z, rnd() * TAU, 1, (rnd() - 0.5) * 0.6, (rnd() - 0.5) * 0.6);
      const spear = rnd() < 0.55;
      const c = pick(IRON, rnd());
      if (spear) {
        body.add(P.cyl, pick(POLE, rnd()), m, place(0, tall / 2, 0, 0, new THREE.Vector3(0.035, tall, 0.035)));
        // The head bent over, melted.
        body.add(P.cone, c, m, place(0.05, tall + 0.04, 0, 0, new THREE.Vector3(0.07, 0.2, 0.05), 0, -1.1));
      } else {
        body.add(P.box, c, m, place(0, tall * 0.4, 0, 0, new THREE.Vector3(0.07, tall * 0.8, 0.018)));
        body.add(P.box, pick(IRON, rnd()), m, place(0, tall * 0.82, 0, 0, new THREE.Vector3(0.22, 0.035, 0.04)));
        body.add(P.cyl, pick(POLE, rnd()), m, place(0, tall * 0.92, 0, 0, new THREE.Vector3(0.035, 0.16, 0.035)));
      }
      body.add(P.blob, pick(SLAG, rnd()), place(x, y + 0.02, z, rnd() * TAU, new THREE.Vector3(0.22, 0.07, 0.18)));
    }
  };

  /** A ballista nobody builds anymore: a double recurved bow over a long rail, a ram's head at its end; sunk at an angle. */
  const ballista = (p: THREE.Vector3): void => {
    const yaw = rnd() * TAU;
    const m = place(p.x, p.y - 0.18, p.z, yaw, 1.25, -0.35 - rnd() * 0.15, (rnd() - 0.5) * 0.4);
    const wood = pick(BRONZE, rnd());
    // The rail, long, and the stock under it.
    body.add(P.box, wood, m, place(0, 0.55, 0.1, 0, new THREE.Vector3(0.16, 0.12, 1.9)));
    body.add(P.box, pick(POLE, rnd()), m, place(0, 0.32, -0.2, 0, new THREE.Vector3(0.3, 0.22, 0.9)));
    // Two splayed legs, one gone under the glass.
    for (const s of [-1, 1]) body.add(P.cyl, pick(POLE, rnd()), m, place(s * 0.28, 0.2, -0.5, 0, new THREE.Vector3(0.07, 0.65, 0.07), 0.3, s * 0.35));
    // The double bow: two pairs of recurved limbs, one over the other.
    for (const y of [0.62, 0.78]) {
      for (const s of [-1, 1]) {
        body.add(P.limb, pick(IRON, rnd()), m, place(s * 0.05, y, 0.75, s > 0 ? 0 : Math.PI / 2, new THREE.Vector3(0.9, 0.9, 0.9)));
        // The tips curl back.
        body.add(P.cone, pick(IRON, rnd()), m, place(s * 0.5, y, 0.32, 0, new THREE.Vector3(0.06, 0.22, 0.06), Math.PI / 2 + 0.6));
      }
    }
    // The ram's head at the rail's end: a block, a horn curled on each side.
    body.add(P.blob, pick(IRON, rnd()), m, place(0, 0.6, 1.1, 0, new THREE.Vector3(0.26, 0.24, 0.3)));
    for (const s of [-1, 1]) body.add(P.limb, pick(BRONZE, rnd()), m, place(s * 0.12, 0.66, 1.06, s > 0 ? -Math.PI / 2 : Math.PI, new THREE.Vector3(0.24, 0.5, 0.24), 0, s * 0.4));
    // A bolt still in the groove, its head molten.
    body.add(P.cyl, pick(POLE, rnd()), m, place(0, 0.64, 0.2, 0, new THREE.Vector3(0.04, 1.3, 0.04), Math.PI / 2));
    body.add(P.blob, pick(SLAG, rnd()), m, place(0, 0.64, 0.88, 0, new THREE.Vector3(0.12, 0.1, 0.16)));
    pool(p, 1.25);
    dull.push({ p: p.clone().setY(p.y + 0.05), s: 1.6, o: 0.05 });
  };

  /** The ring of a fire pit long cold, a last few embers under its ash. */
  const pit = (p: THREE.Vector3): void => {
    for (let i = 0; i < 9; i++) {
      const a = (i / 9) * TAU;
      body.add(P.rock, pick(STONE, rnd()), place(p.x + Math.cos(a) * 0.45, p.y + 0.06, p.z + Math.sin(a) * 0.45, rnd() * TAU, new THREE.Vector3(0.24, 0.16, 0.2)));
    }
    body.add(P.disc, '#1a1416', place(p.x, p.y + 0.02, p.z, 0, new THREE.Vector3(0.8, 1, 0.8)));
    for (let i = 0; i < 4; i++) body.add(P.cyl, '#1d1614', place(p.x, p.y + 0.07, p.z, (i / 4) * Math.PI + rnd() * 0.3, new THREE.Vector3(0.07, 0.6, 0.07), Math.PI / 2));
    embers.push(p.clone().setY(p.y + 0.15));
  };

  /** A pole with a rag of the old red still on it: three tattered strips moving a little. */
  const rag = (p: THREE.Vector3): void => {
    const m = place(p.x, p.y, p.z, rnd() * TAU, 1, (rnd() - 0.5) * 0.18);
    body.add(P.cyl, pick(POLE, rnd()), m, place(0, 1.1, 0, 0, new THREE.Vector3(0.07, 2.2, 0.07)));
    body.add(P.cyl, pick(POLE, rnd()), m, place(0, 2.05, 0.2, 0, new THREE.Vector3(0.05, 0.5, 0.05), Math.PI / 2));
    for (let k = 0; k < 3; k++) body.add(P.box, pick(RAG, rnd()), m, place(0, 1.75 - k * 0.05, 0.08 + k * 0.12, 0, new THREE.Vector3(0.015, 0.55 - k * 0.12, 0.1), 0, 0.06 * (k - 1)));
    body.add(P.rock, pick(STONE, rnd()), place(p.x, p.y + 0.08, p.z, rnd() * TAU, new THREE.Vector3(0.4, 0.2, 0.36)));
  };

  const low = ctx.low;
  const few = o.sparse;
  for (let i = 0; i < (few ? 3 : low ? 4 : 6); i++) {
    const p = take(free);
    if (p) tent(p);
  }
  for (let i = 0; i < (few ? 1 : 3); i++) {
    const p = take(free);
    if (p) ballista(p);
  }
  for (let i = 0; i < (few ? 1 : 2); i++) {
    const p = take(free);
    if (p) rag(p);
  }
  {
    const p = take(free);
    if (p) pit(p);
  }
  // Inside the clearing only small things, off the roads and the chests: melted weapons and a smaller fire ring.
  for (let i = 0; i < (few ? 0 : low ? 4 : 7); i++) {
    const p = take(inner);
    if (p) weapons(p);
  }
  for (let i = 0; i < (few ? 3 : low ? 3 : 6); i++) {
    const p = take(free);
    if (p) weapons(p);
  }

  const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.82, metalness: 0.1, flatShading: true });
  const mesh = body.build(mat, 'camp-body');
  if (mesh) {
    mesh.receiveShadow = true;
    group.add(mesh);
  }
  group.add(glow.mesh);

  let spark = 0;
  return {
    group,
    frame(t) {
      // Now and then a spark lifts off the cold fire's ash.
      if (!ctx.low && t - spark > 0.8) {
        spark = t;
        for (const e of embers) ctx.vfx.burst(e, { n: 1, color: '#ff8a4a', speed: 0.004, up: 0.025, size: 0.05, life: 16, gravity: -0.001, spread: 0.2 });
      }
      glow.begin();
      for (const d of dull) glow.add(d.p.x, d.p.y, d.p.z, EMBER, d.s, d.o);
      for (const e of embers) {
        const k = 0.7 + 0.3 * Math.sin(t * 7 + e.x) * Math.sin(t * 4.1 + e.z);
        glow.add(e.x, e.y, e.z, EMBER, 1.1 * k, 0.24);
      }
      glow.end();
    },
    dispose() {
      mesh?.geometry.dispose();
      mat.dispose();
      glow.mesh.geometry.dispose();
      glow.material.dispose();
    },
  };
};
